import { useEffect, useState, type FormEvent } from "react";
import { normalizePatientName, patientNameAsTyped, type Patient, type PatientRegisterPayload, type PatientSummary, type Sex } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { ageText, fmtDayNumeric, fmtPhone, fullName, initials, sexLabel } from "../lib/format.js";
import { errorMessage, useDebounced, useQuery } from "../lib/useQuery.js";
import { Button, Field, Input, SearchInput, Segmented, Select, Textarea } from "./ui.js";
import { Icon } from "./icons.js";

/** Search box + results list. Typing three letters or a phone fragment shows matches. */
export function PatientSearch({ onPick, autoFocus, placeholder = "Buscar por nombre o celular…" }: { onPick: (p: PatientSummary) => void; autoFocus?: boolean; placeholder?: string }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim());
  const query = useQuery(() => get("patients", { query: { q: dq, limit: 12 } }), [dq], { enabled: dq.length >= 2 });
  return (
    <div className="stack">
      <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} autoFocus={autoFocus} className="lg" aria-label="Buscar paciente" />
      {dq.length >= 2 && (
        <div className="card" style={{ overflow: "hidden" }}>
          {query.loading && !query.data ? (
            <div className="list-row faint">Buscando…</div>
          ) : query.data?.items.length ? (
            query.data.items.map((p) => (
              <button key={p.id} type="button" className="patient-hit" onClick={() => onPick(p)}>
                <span className={`avatar ${p.sex}`}>{initials(p)}</span>
                <span className="grow">
                  <div className="primary">{fullName(p)}</div>
                  <div className="secondary">
                    {sexLabel(p.sex)} · {ageText(p.birth_date)} · nac. {fmtDayNumeric(p.birth_date)}
                    {p.phone ? ` · ${fmtPhone(p.phone)}` : ""}
                  </div>
                </span>
                <Icon name="chevronRight" style={{ color: "var(--ink-3)" }} />
              </button>
            ))
          ) : (
            <div className="list-row faint">Sin coincidencias. Puedes registrar al paciente abajo.</div>
          )}
        </div>
      )}
    </div>
  );
}

export interface PatientFormValues {
  first_name: string;
  paternal_surname: string;
  maternal_surname: string;
  birth_date: string;
  sex: Sex;
  phone: string;
  email: string;
  origin: string;
  notes: string;
}

export const EMPTY_PATIENT: PatientFormValues = {
  first_name: "",
  paternal_surname: "",
  maternal_surname: "",
  birth_date: "",
  sex: "F",
  phone: "",
  email: "",
  origin: "",
  notes: "",
};

export function patientToForm(p: Patient): PatientFormValues {
  return {
    first_name: p.first_name,
    paternal_surname: p.paternal_surname,
    maternal_surname: p.maternal_surname ?? "",
    birth_date: p.birth_date,
    sex: p.sex,
    phone: p.phone ?? "",
    email: p.email ?? "",
    origin: p.origin ?? "",
    notes: p.notes ?? "",
  };
}

/** Name fields as the form shows them: in capitals from the first keystroke, as they will be saved. */
export function withNamesAsTyped(v: PatientFormValues): PatientFormValues {
  return { ...v, first_name: patientNameAsTyped(v.first_name), paternal_surname: patientNameAsTyped(v.paternal_surname), maternal_surname: patientNameAsTyped(v.maternal_surname) };
}

export function toPayload(v: PatientFormValues): PatientRegisterPayload {
  return {
    first_name: normalizePatientName(v.first_name),
    paternal_surname: normalizePatientName(v.paternal_surname),
    maternal_surname: normalizePatientName(v.maternal_surname) || null,
    birth_date: v.birth_date,
    sex: v.sex,
    phone: v.phone.replace(/\D/g, "") || null,
    email: v.email.trim() || null,
    origin: v.origin.trim() || null,
    notes: v.notes.trim() || null,
  };
}

/**
 * Reception fields (meeting 2026-10-31): full name, date of birth, sex, place of origin, phone.
 * Registers (or updates) through the command bus and hands back the saved patient.
 */
export function PatientForm({ initial, patientId, onSaved, onCancel, submitLabel = "Registrar paciente" }: { initial?: PatientFormValues; patientId?: string; onSaved: (p: Patient) => void; onCancel?: () => void; submitLabel?: string }) {
  const [v, setV] = useState<PatientFormValues>(() => withNamesAsTyped(initial ?? EMPTY_PATIENT));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (initial) setV(withNamesAsTyped(initial));
  }, [initial]);
  const set = <K extends keyof PatientFormValues>(k: K, val: PatientFormValues[K]) => setV((s) => ({ ...s, [k]: val }));
  // The laboratory writes patient names in capitals (civil registry); the value itself is upper-cased, not just its look.
  const setName = (k: "first_name" | "paternal_surname" | "maternal_surname", val: string) => set(k, patientNameAsTyped(val));
  const phoneOk = v.phone === "" || v.phone.replace(/\D/g, "").length === 10;
  const valid = v.first_name.trim() && v.paternal_surname.trim() && v.birth_date && phoneOk;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const payload = toPayload(v);
      const saved = patientId ? await run<"patient.update", Patient>("patient.update", { ...payload, patient_id: patientId }) : await run<"patient.register", Patient>("patient.register", payload);
      onSaved(saved);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="stack-lg" noValidate>
      <div className="grid-3">
        <Field label="Nombre(s)">
          <Input value={v.first_name} onChange={(e) => setName("first_name", e.target.value)} autoComplete="off" autoCapitalize="characters" required />
        </Field>
        <Field label="Apellido paterno">
          <Input value={v.paternal_surname} onChange={(e) => setName("paternal_surname", e.target.value)} autoComplete="off" autoCapitalize="characters" required />
        </Field>
        <Field label="Apellido materno" optional>
          <Input value={v.maternal_surname} onChange={(e) => setName("maternal_surname", e.target.value)} autoComplete="off" autoCapitalize="characters" />
        </Field>
      </div>
      <div className="grid-3">
        <Field label="Fecha de nacimiento" hint={v.birth_date ? ageText(v.birth_date) : undefined}>
          <Input type="date" value={v.birth_date} onChange={(e) => set("birth_date", e.target.value)} max={new Date().toISOString().slice(0, 10)} required />
        </Field>
        <Field label="Sexo">
          <Segmented value={v.sex} onChange={(s) => set("sex", s)} options={[{ value: "F", label: "Femenino" }, { value: "M", label: "Masculino" }]} block />
        </Field>
        <Field label="Celular" optional error={phoneOk ? null : "Escribe los 10 dígitos."} hint="Para enviar los resultados por WhatsApp.">
          <Input type="tel" inputMode="numeric" value={v.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10 dígitos" aria-invalid={!phoneOk} />
        </Field>
      </div>
      <div className="grid-3">
        <Field label="Lugar de procedencia" optional>
          <Input value={v.origin} onChange={(e) => set("origin", e.target.value)} placeholder="Colonia o comunidad" />
        </Field>
        <Field label="Correo" optional>
          <Input type="email" inputMode="email" value={v.email} onChange={(e) => set("email", e.target.value)} />
        </Field>
        <Field label="Notas del paciente" optional>
          <Input value={v.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Alergias, observaciones…" />
        </Field>
      </div>
      {error && <p className="err" role="alert" style={{ color: "var(--red)", fontWeight: 500 }}>{error}</p>}
      <div className="row end">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button type="submit" variant="primary" icon="check" busy={busy} disabled={!valid}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** Shown once a patient is chosen: who they are, with a way to change. */
export function SelectedPatient({ patient, onChange }: { patient: PatientSummary; onChange?: () => void }) {
  return (
    <div className="selected-patient">
      <span className={`avatar ${patient.sex}`}>{initials(patient)}</span>
      <div className="grow">
        <div className="h3">{fullName(patient)}</div>
        <div className="small muted">
          {sexLabel(patient.sex)} · {ageText(patient.birth_date)} · nac. {fmtDayNumeric(patient.birth_date)} · {patient.phone ? fmtPhone(patient.phone) : "Sin celular"}
        </div>
      </div>
      {onChange && (
        <Button variant="ghost" size="sm" onClick={onChange}>
          Cambiar
        </Button>
      )}
    </div>
  );
}

/** Condition that selects special reference intervals (ADR 0009). Free text in the contract; two presets here. */
export function ConditionPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const preset = value === "" ? "none" : value === "embarazo" ? "embarazo" : "other";
  return (
    <div className="stack" style={{ gap: 8 }}>
      <Segmented
        value={preset}
        onChange={(p) => onChange(p === "none" ? "" : p === "embarazo" ? "embarazo" : value || "otra")}
        options={[
          { value: "none", label: "Ninguna" },
          { value: "embarazo", label: "Embarazo" },
          { value: "other", label: "Otra" },
        ]}
        block
      />
      {preset === "other" && <Input value={value === "otra" ? "" : value} onChange={(e) => onChange(e.target.value || "otra")} placeholder="Describe la condición" />}
    </div>
  );
}

export { Select as PatientSelect, Textarea as PatientTextarea };
